import { KeyboardArrowLeft as KeyboardArrowLeftIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  Slide,
  SlideProps,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AssemblyIndoorImage from 'src/assets/images/AssemblyIndoor.svg?react';
import AssemblyMacCafeImage from 'src/assets/images/AssemblyMacCafe.svg?react';
import ChannelOutdoorImage from 'src/assets/images/ChannelOutdoor.svg?react';
import { AssemblyLayout } from 'src/components/assemblyLayout';
import { AssemblyScreen } from 'src/components/assemblyScreen';
import { CustomButton } from 'src/components/button/Button';
import { Progress } from 'src/components/progress/Progress';
import { Snackbar } from 'src/components/snackbar';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import { useCreateLocationGroup } from 'src/hooks';
import { IViewChannel } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import { locationGroupCreateEvent } from 'src/utils/google-tag-manager';

import { SelectedOptions } from './AddLocationGroupPage.interface';
import {
  IconContainer,
  ImageButton,
  InputTextField,
  InputTextLabel,
  LayoutButton,
  LayoutTypeWrapper,
  MacCafeIconContainer,
  OptionWrapper,
  OutdoorIconContainer,
  ScreenContainer,
  Step,
  StepContent,
  StepNavigation,
  StepNavigationButtonWrapperRight,
  Wrapper,
} from './AddLocationGroupPage.styled';
import { useAddNewLocationGroup } from './hooks/AddLocationGroupPage.hook';

export const AddLocationGroupPage = () => {
  const createNewLocationGroup = useCreateLocationGroup();
  const { isViewOnly } = useUserRole();

  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);

  const [direction, setDirection] = useState<SlideProps['direction']>('up');

  const {
    isLoadinglayoutTypes,
    isLoadingChannels,
    errorLoadinglayoutTypes,
    errorLoadingChannels,
    layoutTypes,
    channelsData,
    setSelectedLayoutTypeState,
    selectedLayoutTypeState,
  } = useAddNewLocationGroup();

  const [selectedOptions, setSelectedOptions] = useState<
    Partial<SelectedOptions>
  >({
    locationGroupName: '',
  });

  const handleNext = () => {
    setDirection('up');
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setDirection('down');
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCancel = () => {
    navigate(PATHS.LOCATION_GROUPS);
    setActiveStep(0);
    setSelectedOptions({});
  };

  const handleDone = async () => {
    if (
      selectedOptions.locationGroupName &&
      selectedOptions.layoutType &&
      selectedOptions.channel
    ) {
      const request = {
        name: selectedOptions.locationGroupName.trim(),
        channel_name: selectedOptions.channel.channel_name,
        layout_type_id: selectedOptions.layoutType.id,
      };
      const result = await createNewLocationGroup.mutateAsync(request);
      locationGroupCreateEvent(
        result.id,
        selectedOptions.locationGroupName.trim(),
        selectedOptions.layoutType.name,
        selectedOptions.channel.screens,
      );

      navigate(PATHS.LOCATION_GROUP_TIMINGS(result.id));
      Snackbar.success(
        `${selectedOptions.locationGroupName.trim()} ${
          lang.successfullyCreated
        }`,
      );
    }
  };

  const handleSelect = (key: string, value: any) => {
    setSelectedOptions((prevSelectedOptions) => ({
      ...prevSelectedOptions,
      [key]: value,
    }));
  };

  if (isLoadinglayoutTypes || isLoadingChannels) return <Progress />;

  if (errorLoadinglayoutTypes || errorLoadingChannels)
    // TODO: Replace with error component
    return <p>{lang.error}</p>;

  const isDisabled = () => {
    if (
      activeStep === 0 &&
      selectedOptions?.locationGroupName &&
      selectedOptions.locationGroupName.trim() &&
      selectedOptions.layoutType?.id
    )
      return true;
    else if (activeStep === 1 && selectedOptions.channel?.channel_name)
      return true;
    else return false;
  };

  const steps = [
    {
      title: "Let's create a location group",
      content: (
        <Box>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={6}>
              <InputTextLabel>{lang.locationGroupName} </InputTextLabel>
              <InputTextField
                data-testid="location-group-name"
                variant="outlined"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSelectedOptions((prevSelectedOptions) => ({
                    ...prevSelectedOptions,
                    locationGroupName: event.target.value as string,
                  }))
                }
                value={selectedOptions.locationGroupName}
              />
            </Grid>
          </Grid>
          <LayoutTypeWrapper>
            {layoutTypes?.map((layoutType, index) => {
              return (
                <ImageButton
                  key={index}
                  selected={selectedOptions?.layoutType?.id === layoutType.id}
                  data-testid={`addLocationGroupLayout-${index}`}
                  onClick={() => {
                    if (selectedLayoutTypeState !== layoutType.id) {
                      setSelectedLayoutTypeState(layoutType.id);
                      handleSelect('layoutType', layoutType);
                    }
                  }}>
                  {(() => {
                    switch (layoutType.name) {
                      case 'Indoor':
                        return (
                          <IconContainer>
                            <AssemblyIndoorImage />
                          </IconContainer>
                        );
                      case 'Outdoor':
                        return (
                          <OutdoorIconContainer>
                            <ChannelOutdoorImage />
                          </OutdoorIconContainer>
                        );
                      default:
                        return (
                          <MacCafeIconContainer>
                            <AssemblyMacCafeImage />
                          </MacCafeIconContainer>
                        );
                    }
                  })()}
                  {layoutType.name}
                </ImageButton>
              );
            })}
          </LayoutTypeWrapper>
        </Box>
      ),
    },
    {
      title: 'Select screen configuration',
      content: (
        <OptionWrapper>
          {channelsData?.map((channel: IViewChannel, index: number) => (
            <LayoutButton
              key={index}
              selected={
                selectedOptions.channel?.channel_name === channel.channel_name
              }
              onClick={() => {
                handleSelect('channel', channel);
              }}
              data-testid={`addLocationGroupChannel-${index}`}
              disabled={false}>
              {channel.channel_name}
              <ScreenContainer>
                {channel.screens && (
                  <AssemblyLayout>
                    {channel.screens.map((screen, index) => (
                      <AssemblyScreen
                        key={index}
                        height={screen.height}
                        width={screen.width}
                        scale={0.05}
                        selectable={false}
                        type="screen"></AssemblyScreen>
                    ))}
                  </AssemblyLayout>
                )}
              </ScreenContainer>
              {channel.screenConfigurationLabel}
            </LayoutButton>
          ))}
        </OptionWrapper>
      ),
    },
  ];

  return (
    <Wrapper>
      <Box sx={{ position: 'absolute', top: 0, right: 0, margin: 3 }}>
        {isViewOnly && <ViewOnlyAlert data-testid="VisibilityIcon" />}
      </Box>
      {steps.map((step, i) => (
        <Slide
          key={i}
          direction={direction}
          in={activeStep === i}
          timeout={{ enter: 200, exit: 0 }}
          data-testid={`addLocationGroupSlide-${i}`}>
          <Step sx={{ maxWidth: 'lg' }}>
            <Typography variant="h1" gutterBottom>
              {step.title}
            </Typography>
            <StepContent>{steps[activeStep].content}</StepContent>
            <StepNavigation>
              <Box>
                {activeStep > 0 && (
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    startIcon={<KeyboardArrowLeftIcon />}>
                    {lang.back}
                  </Button>
                )}
              </Box>
              {!isViewOnly && (
                <StepNavigationButtonWrapperRight>
                  <CustomButton variant="text" onClick={handleCancel}>
                    {lang.cancel}
                  </CustomButton>
                  {activeStep !== steps.length - 1 && (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={!isDisabled()}>
                      {lang.next}
                    </Button>
                  )}
                  {activeStep === steps.length - 1 && (
                    <Button
                      variant="contained"
                      onClick={handleDone}
                      disabled={!selectedOptions.channel}>
                      {lang.create}
                    </Button>
                  )}
                </StepNavigationButtonWrapperRight>
              )}
            </StepNavigation>
          </Step>
        </Slide>
      ))}
    </Wrapper>
  );
};
